/**
 * Klient för admin-konfigurerbara default-värden (S-04-22a backend +
 * S-04-22 frontend).
 *
 * Wrappar:
 *  - `GET /api/admin/settings`         — lista nyckel/värdepar
 *  - `PUT /api/admin/settings/{key}`   — upsert med audit (kräver MFA-
 *    aktiv admin via befintliga muterande-flöden)
 *
 * Hard-rules (CLAUDE.md):
 *  - Cookie-baserad auth + CSRF-header på alla muterande anrop.
 *  - Inga råa databas-ID:n exponeras (key/value är kategoriska).
 *  - Användartexter på svenska.
 */
import { csrfHeader } from '../auth/csrf';

const API_BASE = '/api/admin/settings';

/**
 * Schema för `map.default`. Backend validerar zoom 0–22, lat ±90,
 * lng ±180 och avvisar extra fält. Klienten dubblerar valideringen
 * lokalt så att admin inte spamar PUT med uppenbart ogiltiga värden.
 */
export type MapDefault = {
  zoom: number;
  lat: number;
  lng: number;
};

export type AppSettingItem = {
  key: string;
  value: unknown;
  updatedAt: string;
};

export type ListSettingsResult =
  | { kind: 'ok'; items: AppSettingItem[] }
  | { kind: 'unauthorized' }
  | { kind: 'forbidden' }
  | { kind: 'error'; message: string };

export type SaveSettingResult =
  | { kind: 'ok'; item: AppSettingItem }
  | { kind: 'unauthorized' }
  | { kind: 'forbidden' }
  | { kind: 'invalid'; field: 'zoom' | 'lat' | 'lng' | 'payload' | 'key' }
  | { kind: 'error'; message: string };

/**
 * S-02-11/S-04-23 — walker-app version-konfig.
 * Backend-schema: { versionCode: Int >= 1, versionName: semver "X.Y.Z" }
 */
export type WalkerAppMinSupported = {
  versionCode: number;
  versionName: string;
};

/**
 * S-02-11/S-04-23 — walker-app senaste version.
 * downloadUrl MÅSTE börja med 'https://harden.compuna.se/apk/' (backend
 * + klient-side validering, skydd mot kompromettat admin-konto som
 * dirigerar walker-användare till angripare-domän).
 */
export type WalkerAppLatest = {
  versionCode: number;
  versionName: string;
  downloadUrl: string;
};

export type SaveWalkerAppMinSupportedResult =
  | { kind: 'ok'; item: AppSettingItem }
  | { kind: 'unauthorized' }
  | { kind: 'forbidden' }
  | { kind: 'invalid'; field: 'versionCode' | 'versionName' | 'payload' }
  | { kind: 'error'; message: string };

export type SaveWalkerAppLatestResult =
  | { kind: 'ok'; item: AppSettingItem }
  | { kind: 'unauthorized' }
  | { kind: 'forbidden' }
  | { kind: 'invalid'; field: 'versionCode' | 'versionName' | 'downloadUrl' | 'payload' }
  | { kind: 'error'; message: string };

const APK_URL_PREFIX = 'https://harden.compuna.se/apk/';
const SEMVER_PATTERN = /^\d+\.\d+\.\d+$/;

function messageForCode(code: string): string {
  switch (code) {
    case 'unauthorized':
      return 'Sessionen har gått ut. Logga in igen.';
    case 'forbidden':
      return 'Saknar behörighet att ändra inställningar.';
    case 'invalid_key':
      return 'Inställningen finns inte längre.';
    case 'invalid_payload':
    case 'invalid_request_body':
      return 'Ogiltigt format på inställningen.';
    case 'unknown_field':
      return 'Inställningen innehåller okända fält.';
    case 'zoom_out_of_range':
      return 'Zoom måste ligga mellan 0 och 22.';
    case 'lat_out_of_range':
      return 'Latitud måste ligga mellan -90 och 90.';
    case 'lng_out_of_range':
      return 'Longitud måste ligga mellan -180 och 180.';
    case 'invalid_zoom':
      return 'Zoom måste vara ett tal.';
    case 'invalid_lat':
      return 'Latitud måste vara ett tal.';
    case 'invalid_lng':
      return 'Longitud måste vara ett tal.';
    case 'invalid_version_code':
      return 'Version-kod måste vara ett heltal.';
    case 'version_code_out_of_range':
      return 'Version-kod måste vara minst 1.';
    case 'invalid_version_name':
      return 'Version-namn måste anges.';
    case 'version_name_not_semver':
      return 'Version-namn måste följa formatet X.Y.Z (t.ex. 0.1.0).';
    case 'invalid_download_url':
      return 'Download-URL måste anges.';
    case 'download_url_wrong_prefix':
      return `Download-URL måste börja med ${APK_URL_PREFIX}`;
    default:
      return 'Något gick fel. Försök igen.';
  }
}

export async function listSettings(): Promise<ListSettingsResult> {
  let response: Response;
  try {
    response = await fetch(API_BASE, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch (e) {
    return { kind: 'error', message: 'Nätverksfel — kontrollera anslutningen.' };
  }

  if (response.status === 401) return { kind: 'unauthorized' };
  if (response.status === 403) return { kind: 'forbidden' };
  if (!response.ok) {
    return { kind: 'error', message: messageForCode('unknown') };
  }

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return { kind: 'error', message: messageForCode('unknown') };
  }
  const items = (body as { items?: AppSettingItem[] }).items ?? [];
  return { kind: 'ok', items };
}

export async function saveMapDefault(value: MapDefault): Promise<SaveSettingResult> {
  // Lokal validering — duplicerar backend-checken så att uppenbart
  // ogiltiga värden aldrig genererar en muterande HTTP-request.
  if (!Number.isFinite(value.zoom) || value.zoom < 0 || value.zoom > 22) {
    return { kind: 'invalid', field: 'zoom' };
  }
  if (!Number.isFinite(value.lat) || value.lat < -90 || value.lat > 90) {
    return { kind: 'invalid', field: 'lat' };
  }
  if (!Number.isFinite(value.lng) || value.lng < -180 || value.lng > 180) {
    return { kind: 'invalid', field: 'lng' };
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}/map.default`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...csrfHeader(),
      },
      body: JSON.stringify({ value }),
    });
  } catch (e) {
    return { kind: 'error', message: 'Nätverksfel — försök igen.' };
  }

  if (response.status === 401) return { kind: 'unauthorized' };
  if (response.status === 403) return { kind: 'forbidden' };

  if (response.status === 400) {
    let code = 'invalid_payload';
    try {
      const errorBody = (await response.json()) as { error?: string };
      if (typeof errorBody.error === 'string') code = errorBody.error;
    } catch {
      // ignore
    }
    if (code === 'zoom_out_of_range' || code === 'invalid_zoom') {
      return { kind: 'invalid', field: 'zoom' };
    }
    if (code === 'lat_out_of_range' || code === 'invalid_lat') {
      return { kind: 'invalid', field: 'lat' };
    }
    if (code === 'lng_out_of_range' || code === 'invalid_lng') {
      return { kind: 'invalid', field: 'lng' };
    }
    if (code === 'invalid_key') return { kind: 'invalid', field: 'key' };
    return { kind: 'invalid', field: 'payload' };
  }

  if (!response.ok) {
    return { kind: 'error', message: messageForCode('unknown') };
  }

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return { kind: 'error', message: messageForCode('unknown') };
  }
  return { kind: 'ok', item: body as AppSettingItem };
}

/**
 * Konvertera ett värde-fält från `app_settings`-svaret till en typad
 * `MapDefault`. Returnerar null om payload inte matchar schemat — caller
 * kan då falla tillbaka på kompilerings-default (Hisingsbacka centrum).
 */
export function parseMapDefault(value: unknown): MapDefault | null {
  if (typeof value !== 'object' || value === null) return null;
  const obj = value as Record<string, unknown>;
  const { zoom, lat, lng } = obj;
  if (
    typeof zoom !== 'number' ||
    typeof lat !== 'number' ||
    typeof lng !== 'number' ||
    !Number.isFinite(zoom) ||
    !Number.isFinite(lat) ||
    !Number.isFinite(lng)
  ) {
    return null;
  }
  if (zoom < 0 || zoom > 22) return null;
  if (lat < -90 || lat > 90) return null;
  if (lng < -180 || lng > 180) return null;
  return { zoom, lat, lng };
}

/**
 * S-04-23: spara walker-app min-supported version. Backend validerar
 * versionCode (int >= 1) + versionName (semver). Lokal validering
 * speglar backend-reglerna så uppenbart ogiltiga värden inte genererar
 * en muterande request.
 */
export async function saveWalkerAppMinSupported(
  value: WalkerAppMinSupported,
): Promise<SaveWalkerAppMinSupportedResult> {
  if (!Number.isInteger(value.versionCode) || value.versionCode < 1) {
    return { kind: 'invalid', field: 'versionCode' };
  }
  if (typeof value.versionName !== 'string' || !SEMVER_PATTERN.test(value.versionName)) {
    return { kind: 'invalid', field: 'versionName' };
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}/walker_app.min_supported`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...csrfHeader(),
      },
      body: JSON.stringify({ value }),
    });
  } catch (e) {
    return { kind: 'error', message: 'Nätverksfel — försök igen.' };
  }

  if (response.status === 401) return { kind: 'unauthorized' };
  if (response.status === 403) return { kind: 'forbidden' };

  if (response.status === 400) {
    let code = 'invalid_payload';
    try {
      const errorBody = (await response.json()) as { error?: string };
      if (typeof errorBody.error === 'string') code = errorBody.error;
    } catch {
      // ignore
    }
    if (code === 'invalid_version_code' || code === 'version_code_out_of_range') {
      return { kind: 'invalid', field: 'versionCode' };
    }
    if (code === 'invalid_version_name' || code === 'version_name_not_semver') {
      return { kind: 'invalid', field: 'versionName' };
    }
    return { kind: 'invalid', field: 'payload' };
  }

  if (!response.ok) {
    return { kind: 'error', message: messageForCode('unknown') };
  }

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return { kind: 'error', message: messageForCode('unknown') };
  }
  return { kind: 'ok', item: body as AppSettingItem };
}

/**
 * S-04-23: spara walker-app senaste version. URL-prefix-låsningen
 * (https://harden.compuna.se/apk/) skyddar mot kompromettat admin-
 * konto som dirigerar walker-användare till angripare-domän. CLAUDE.md
 * hard-rule "All persondata i Sverige/EU".
 */
export async function saveWalkerAppLatest(
  value: WalkerAppLatest,
): Promise<SaveWalkerAppLatestResult> {
  if (!Number.isInteger(value.versionCode) || value.versionCode < 1) {
    return { kind: 'invalid', field: 'versionCode' };
  }
  if (typeof value.versionName !== 'string' || !SEMVER_PATTERN.test(value.versionName)) {
    return { kind: 'invalid', field: 'versionName' };
  }
  if (typeof value.downloadUrl !== 'string' || !value.downloadUrl.startsWith(APK_URL_PREFIX)) {
    return { kind: 'invalid', field: 'downloadUrl' };
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}/walker_app.latest`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...csrfHeader(),
      },
      body: JSON.stringify({ value }),
    });
  } catch (e) {
    return { kind: 'error', message: 'Nätverksfel — försök igen.' };
  }

  if (response.status === 401) return { kind: 'unauthorized' };
  if (response.status === 403) return { kind: 'forbidden' };

  if (response.status === 400) {
    let code = 'invalid_payload';
    try {
      const errorBody = (await response.json()) as { error?: string };
      if (typeof errorBody.error === 'string') code = errorBody.error;
    } catch {
      // ignore
    }
    if (code === 'invalid_version_code' || code === 'version_code_out_of_range') {
      return { kind: 'invalid', field: 'versionCode' };
    }
    if (code === 'invalid_version_name' || code === 'version_name_not_semver') {
      return { kind: 'invalid', field: 'versionName' };
    }
    if (code === 'invalid_download_url' || code === 'download_url_wrong_prefix') {
      return { kind: 'invalid', field: 'downloadUrl' };
    }
    return { kind: 'invalid', field: 'payload' };
  }

  if (!response.ok) {
    return { kind: 'error', message: messageForCode('unknown') };
  }

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return { kind: 'error', message: messageForCode('unknown') };
  }
  return { kind: 'ok', item: body as AppSettingItem };
}

/**
 * Parsar walker_app.min_supported från app_settings-svaret. Returnerar
 * null om payload inte matchar schemat — caller kan då visa fallback
 * eller tom form.
 */
export function parseWalkerAppMinSupported(value: unknown): WalkerAppMinSupported | null {
  if (typeof value !== 'object' || value === null) return null;
  const obj = value as Record<string, unknown>;
  const { versionCode, versionName } = obj;
  if (typeof versionCode !== 'number' || !Number.isInteger(versionCode) || versionCode < 1) {
    return null;
  }
  if (typeof versionName !== 'string' || !SEMVER_PATTERN.test(versionName)) return null;
  return { versionCode, versionName };
}

export function parseWalkerAppLatest(value: unknown): WalkerAppLatest | null {
  if (typeof value !== 'object' || value === null) return null;
  const obj = value as Record<string, unknown>;
  const { versionCode, versionName, downloadUrl } = obj;
  if (typeof versionCode !== 'number' || !Number.isInteger(versionCode) || versionCode < 1) {
    return null;
  }
  if (typeof versionName !== 'string' || !SEMVER_PATTERN.test(versionName)) return null;
  if (typeof downloadUrl !== 'string' || !downloadUrl.startsWith(APK_URL_PREFIX)) return null;
  return { versionCode, versionName, downloadUrl };
}

export function describeWalkerAppMinSupportedError(
  result: SaveWalkerAppMinSupportedResult,
): string {
  switch (result.kind) {
    case 'ok':
      return '';
    case 'unauthorized':
      return messageForCode('unauthorized');
    case 'forbidden':
      return messageForCode('forbidden');
    case 'invalid':
      switch (result.field) {
        case 'versionCode':
          return messageForCode('version_code_out_of_range');
        case 'versionName':
          return messageForCode('version_name_not_semver');
        default:
          return messageForCode('invalid_payload');
      }
    case 'error':
      return result.message;
  }
}

export function describeWalkerAppLatestError(result: SaveWalkerAppLatestResult): string {
  switch (result.kind) {
    case 'ok':
      return '';
    case 'unauthorized':
      return messageForCode('unauthorized');
    case 'forbidden':
      return messageForCode('forbidden');
    case 'invalid':
      switch (result.field) {
        case 'versionCode':
          return messageForCode('version_code_out_of_range');
        case 'versionName':
          return messageForCode('version_name_not_semver');
        case 'downloadUrl':
          return messageForCode('download_url_wrong_prefix');
        default:
          return messageForCode('invalid_payload');
      }
    case 'error':
      return result.message;
  }
}

/**
 * Användarvänligt felmeddelande för en `SaveSettingResult`. Caller
 * sätter detta som inline-error i UI:t.
 */
export function describeSaveError(result: SaveSettingResult): string {
  switch (result.kind) {
    case 'ok':
      return '';
    case 'unauthorized':
      return messageForCode('unauthorized');
    case 'forbidden':
      return messageForCode('forbidden');
    case 'invalid':
      switch (result.field) {
        case 'zoom':
          return messageForCode('zoom_out_of_range');
        case 'lat':
          return messageForCode('lat_out_of_range');
        case 'lng':
          return messageForCode('lng_out_of_range');
        case 'key':
          return messageForCode('invalid_key');
        default:
          return messageForCode('invalid_payload');
      }
    case 'error':
      return result.message;
  }
}
